import React from "react";
import { useForm } from "react-hook-form";
import { Dialog } from "@headlessui/react";
import { MdClose, MdInfoOutline } from "react-icons/md";
import { HiDownload } from "react-icons/hi";
import useDownloadFile from "../../hooks/useDownloadFile";
import { Button, DateInput, RadioButton, ToolTip } from "../ui";
import InlineLoader from "../loader/InlineLoader";
import moment from "moment";
import toast from "react-hot-toast";

const DownloadAttendanceModal = ({ show, setShow }) => {
  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      month_and_year: new Date(),
      report_type: "",
    },
  });

  const selectedReportType = watch("report_type");
  const monthYear = watch("month_and_year");

  const handleError = async (err) => {
    if (err?.response?.data instanceof Blob) {
      const text = await err.response.data.text();
      try {
        const errorData = JSON.parse(text);
        toast.error(errorData.message || "Something went wrong");
      } catch (e) {
        toast.error("Something went wrong");
      }
    } else {
      toast.error(
        err?.response?.data?.message || err?.message || "Something went wrong"
      );
    }
  };

  const musterDownload = useDownloadFile({
    url: "muster_attendance_report",
    onError: handleError,
  });

  const detailedDownload = useDownloadFile({
    url: "all_employee_attendance_report",
    onError: handleError,
  });

  const isDownloading = musterDownload.reportDownload.isLoading || detailedDownload.reportDownload.isLoading;

  const onDownload = (data) => {
    const formattedDate = moment(data.month_and_year).format("MMM_YYYY");
    const filename = data.report_type === "muster" 
      ? `Muster_Report_${formattedDate}.xlsx` 
      : `Detailed_Report_${formattedDate}.xlsx`;

    const payload = {
      month_year: data.month_and_year,
      external_filename: filename,
    };

    if (data.report_type === "muster") {
      musterDownload.reportDownload.mutate(payload);
    } else if (data.report_type === "detailed") {
      detailedDownload.reportDownload.mutate(payload);
    }
  };

  const handleClose = () => setShow(false);

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <div className="flex min-h-screen items-center justify-center p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-50 transition-opacity" />

        <div className="relative bg-white rounded-lg max-w-md w-full shadow-xl transform transition-all">
          <form onSubmit={handleSubmit(onDownload)}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b rounded-t-lg bg-white">
              <Dialog.Title className="text-lg font-semibold flex items-center text-gray-800">
                <HiDownload className="w-5 h-5 mr-3 text-blue-600" />
                Download Reports
              </Dialog.Title>
              <button
                type="button"
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <MdClose className="w-6 h-6" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6 min-h-[300px]">
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Select the month and choose a report type to download.
                </p>
                
                <DateInput
                  control={control}
                  name="month_and_year"
                  label="Select Month & Year"
                  rules={{ required: "Month and year is required" }}
                  showMonthYearPicker
                  dateFormat="MM/yyyy"
                  maxDate={new Date()}
                  placeholderText="Click to select month & year"
                  className="w-full"
                />

                <div className="space-y-3 pt-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center">
                    Report Type <span className="text-red-600 ml-1">*</span>
                  </label>
                  
                  <div className="grid grid-cols-1 gap-3">
                    <div className="flex items-center space-x-2">
                      <RadioButton
                        control={control}
                        name="report_type"
                        value="muster"
                        label="Muster Report"
                        rules={{ required: "Please select a report type" }}
                      />
                      <ToolTip content="A grid-view report (single sheet) showing a daily status overview (Present, Absent, Leave, etc.) for all employees throughout the selected month.">
                        <MdInfoOutline className="text-gray-400 w-4 h-4 cursor-help" />
                      </ToolTip>
                    </div>

                    <div className="flex items-center space-x-2">
                      <RadioButton
                        control={control}
                        name="report_type"
                        value="detailed"
                        label="Detailed Report"
                        rules={{ required: "Please select a report type" }}
                      />
                      <ToolTip content="A comprehensive chronological report with multiple sheets (one for each employee), showing precise in/out timings, total working hours, and shift-specific details like late marks and grace periods.">
                        <MdInfoOutline className="text-gray-400 w-4 h-4 cursor-help" />
                      </ToolTip>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t rounded-b-lg">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isDownloading || !selectedReportType || !monthYear}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 shrink-0"
              >
                {isDownloading ? (
                  <>
                    <InlineLoader className="text-white mr-2 h-4 w-4" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <HiDownload className="mr-2" />
                    Download
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Dialog>
  );
};

export default DownloadAttendanceModal;



